import { createAsyncThunk } from '@reduxjs/toolkit'
import { convertWidgetTypeToChartType } from 'utils/vizServiceChartTypeConverter'
import { DashboardWidgetType } from 'features/dashboard/types'
import axios from 'axios'
import * as API from 'api'

export interface GenerateVizLinkParam {
  widgetSetting: DashboardWidgetType
}

export interface GenerateVizLinkResponse {
  url: string
}

interface OpenVizFormParams {
  topicId: string
  chartType: string
}

export interface GenerateVizWebhookResponse {
  url: string
  chartType: string
  topicId: string
}

interface FetchVisualizationUrlParams {
  topicId: string
  chartType: string
}

const VIZ_SERVICE_CONFIG = {
  BASE_URL: process.env.REACT_APP_AUTOMATE_VIZ_SERVICE_FORM_URL,
  TIMEOUT_MS: 300000
} as const

const buildVisualizationFormUrl = (topicId: string, chartType: string): string => {
  const baseUrl = VIZ_SERVICE_CONFIG.BASE_URL
  const allParams: Record<string, string> = {
    topicId,
    chartType
  }
  const params = new URLSearchParams(allParams)
  return `${baseUrl}?${params.toString()}`
}

const validateWidgetSetting = (widgetSetting: DashboardWidgetType): string => {
  if (!widgetSetting) {
    throw new Error('Widget setting is required')
  }

  if (!widgetSetting.topicInfos?.length) {
    throw new Error('No topic information available for viz link generation')
  }

  const topicId = widgetSetting.topicInfos[0]?.topicId
  if (!topicId) {
    throw new Error('No valid topic ID found')
  }

  return topicId.toString()
}

const openVizForm = ({
  topicId,
  chartType
}: OpenVizFormParams): {
  url: string
  needsWorkflowTab: boolean
  workflowExecutionUrl?: string
} => {
  const fullUrl = buildVisualizationFormUrl(topicId, chartType)

  const vizFormTab = window.open(fullUrl, '_blank')
  if (!vizFormTab) {
    console.warn('Visualization form tab was blocked.')
  }

  const workflowExecutionUrl =
    process.env.REACT_APP_AUTOMATE_VIZ_SERVICE_FORM_WORKFLOW_EXECUTION_URL
  let needsWorkflowTab = false

  if (workflowExecutionUrl) {
    const workflowExecutionTab = window.open(workflowExecutionUrl, '_blank')
    if (!workflowExecutionTab) {
      console.warn('Workflow execution tab was blocked.')
      needsWorkflowTab = true
    }
  }

  return {
    url: fullUrl,
    needsWorkflowTab,
    workflowExecutionUrl: needsWorkflowTab ? workflowExecutionUrl : undefined
  }
}

export interface GenerateVizLinkFormResponse extends GenerateVizLinkResponse {
  needsWorkflowTab?: boolean
  workflowExecutionUrl?: string
}

export const generateVizLinkForm = createAsyncThunk<
  GenerateVizLinkFormResponse,
  GenerateVizLinkParam,
  { rejectValue: API.FetchErrorResponse | undefined }
>('visualization/generateVizLinkForm', async ({ widgetSetting }, { rejectWithValue }) => {
  try {
    const topicId = validateWidgetSetting(widgetSetting)
    const chartType = convertWidgetTypeToChartType(widgetSetting)
    if (!topicId || !chartType) {
      throw new Error('Invalid widget setting: Missing topicId or chartType')
    }

    const result = openVizForm({
      topicId,
      chartType
    })

    return {
      url: result.url,
      needsWorkflowTab: result.needsWorkflowTab,
      workflowExecutionUrl: result.workflowExecutionUrl
    }
  } catch (error: unknown) {
    console.error('Failed to open visualization:', error)

    let errorMessage = 'Failed to open visualization'

    if (error instanceof Error) {
      errorMessage = error.message
    }

    return rejectWithValue({
      error: errorMessage,
      status: undefined
    } as API.FetchErrorResponse)
  }
})

const fetchVisualizationUrl = async ({
  topicId,
  chartType
}: FetchVisualizationUrlParams): Promise<string> => {
  const params = {
    topicId,
    chartType
  }

  const response = await axios.get(process.env.REACT_APP_AUTOMATE_VIZ_SERVICE_WEBHOOK_URL!, {
    params,
    timeout: VIZ_SERVICE_CONFIG.TIMEOUT_MS
  })

  if (!response.data || typeof response.data !== 'string') {
    throw new Error('No URL returned from visualization service')
  }

  return response.data
}

export const generateVizLinkWebhook = createAsyncThunk<
  GenerateVizLinkResponse,
  GenerateVizLinkParam,
  { rejectValue: API.FetchErrorResponse | undefined }
>('visualization/generateVizLinkWebhook', async ({ widgetSetting }, { rejectWithValue }) => {
  try {
    const topicId = validateWidgetSetting(widgetSetting)
    const chartType = convertWidgetTypeToChartType(widgetSetting)
    const url = await fetchVisualizationUrl({ topicId, chartType })

    return {
      url,
      chartType,
      topicId
    }
  } catch (error: unknown) {
    console.error('Failed to generate viz link:', error)

    let errorMessage = 'Failed to generate visualization link'

    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out - please try again'
      } else if (error.response) {
        errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`
      } else if (error.request) {
        errorMessage = 'No response received from server'
      }
    } else if (error instanceof Error) {
      errorMessage = error.message
    }

    return rejectWithValue({
      error: errorMessage,
      status: axios.isAxiosError(error) ? error.response?.status : undefined
    } as API.FetchErrorResponse)
  }
})
